import React, { useState } from "react";
import { useCart } from "../../lib/cart";
import { Tag, X, Check } from "lucide-react";

/**
 * Single-coupon entry for the cart. No stacking — applying a code replaces
 * any previous one. Validation + discount are computed server-side.
 */
export default function CouponField() {
  const { coupon, applyCoupon, removeCoupon } = useCart();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const apply = async () => {
    const c = code.trim();
    if (!c) return;
    setBusy(true);
    setErr("");
    const res = await applyCoupon(c);
    if (!res.ok) setErr(res.message || "Invalid coupon");
    else setCode("");
    setBusy(false);
  };

  if (coupon?.valid) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2.5 border border-emerald-500/30 bg-emerald-500/10 rounded-md" data-testid="coupon-applied">
        <div className="flex items-center gap-2 min-w-0">
          <Check size={15} className="text-emerald-500 shrink-0" />
          <span className="text-sm font-mono text-emerald-400 truncate" data-testid="coupon-applied-code">{coupon.code}</span>
          {coupon.description && <span className="text-xs text-zinc-500 truncate hidden sm:inline">· {coupon.description}</span>}
        </div>
        <button onClick={removeCoupon} data-testid="coupon-remove-btn" className="text-xs text-zinc-400 hover:text-red-500 inline-flex items-center gap-1 shrink-0">
          <X size={13} /> Remove
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
          <input
            data-testid="coupon-input"
            value={code}
            onChange={(e) => { setCode(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && apply()}
            placeholder="Coupon code"
            className="w-full h-10 pl-9 pr-3 bg-white/5 border border-white/15 text-white text-sm rounded-md focus:outline-none focus:border-red-500 transition uppercase placeholder:normal-case placeholder:text-zinc-500"
          />
        </div>
        <button
          onClick={apply}
          disabled={busy || !code.trim()}
          data-testid="coupon-apply-btn"
          className="h-10 px-4 text-sm bg-white/10 hover:bg-white/20 rounded-md disabled:opacity-50 whitespace-nowrap"
        >
          {busy ? "…" : "Apply"}
        </button>
      </div>
      {err && <p className="mt-1.5 text-xs text-red-400" data-testid="coupon-error">{err}</p>}
    </div>
  );
}
